// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import PropTypes from "prop-types";

import { createExportQueryParams } from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import {
  useDevicesApi,
  useOrganizationsApi,
  useAppsApi,
} from "containers/Request/hooks";
import { useSearchParams } from "hooks/utils";

import { DeviceModelApplicationsRend } from "./DeviceModelApplicationsRend";

/* utils */
function prepareApplicationData(app) {
  return {
    id: app.id,
    icon: app.icon,
    name: app.name,
  };
}
export function DeviceModelApplicationsData({ forAdmin = false }) {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const { id: devid } = useParams();
  const queryParams = useSearchParams();
  const {
    exportDeviceModelApplicationsPaginated,
    getDeviceModel,
    getDeviceFamily,
  } = useDevicesApi();
  const { getOrganizationPublicInfo } = useOrganizationsApi();
  const { getPublishedApplicationsPaginated } = useAppsApi();

  const createPublishedApplicationsQueryParams = () => ({
    ...queryParams,
    include_test_apps: true,
    distributed_by: deviceInfo?.distributorName,
    distributed_on_models: deviceInfo?.deviceModelName,
    distributed_on_families: deviceInfo?.familyName,
  });

  const updateData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { results: appsResults, meta: appsMeta } =
        await getPublishedApplicationsPaginated(
          createPublishedApplicationsQueryParams(queryParams),
          forAdmin
        );

      setCount(appsMeta.count);
      setApplications(appsResults.map(prepareApplicationData));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const onExport = async ({ fileFormat }) => {
    const exportQueryParams = createExportQueryParams({
      fileFormat,
      queryParams: createPublishedApplicationsQueryParams(queryParams),
    });

    await exportDeviceModelApplicationsPaginated(exportQueryParams, forAdmin);
  };

  useEffect(() => {
    const updateDeviceModelName = async () => {
      setLoading(true);
      setError(null);

      try {
        const deviceModel = await getDeviceModel(devid, forAdmin);
        const deviceFamily = await getDeviceFamily(
          deviceModel.device_family.id,
          forAdmin
        );
        const { results: organization } = await getOrganizationPublicInfo(
          deviceFamily.owner.id
        );

        setDeviceInfo({
          deviceModelName: deviceModel.name,
          distributorName: organization?.name,
          familyName: deviceFamily.name,
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    updateDeviceModelName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deviceInfo) {
      updateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, deviceInfo]);

  return (
    <RestrictedArea
      allowed={[
        ROLES.moderator.moderator,
        forAdmin
          ? ROLES.administrator.deviceAdmin
          : ROLES.distributor.distributionPlanner,
      ]}
      showFallback={true}
    >
      <DeviceModelApplicationsRend
        applicationsData={applications}
        count={count}
        error={error}
        loading={loading}
        onExport={onExport}
      />
    </RestrictedArea>
  );
}

DeviceModelApplicationsData.propTypes = {
  forAdmin: PropTypes.bool,
};
